import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import * as R from 'ramda';

import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import VirtualizedAutocomplete from './VirtualizedAutocomplete';

const columns = [
  {
    field: 'customerName',
    headerName: 'Customer',
    maxWidth: 420,
    minWidth: 180,
    renderCell: cellParams => (
      <Tooltip arrow placement="bottom" title={cellParams.value}>
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          {cellParams.value}
        </Box>
      </Tooltip>
    ),
    width: 420,
  },
  {
    field: 'customerIdn',
    headerName: 'Customer ID',
    maxWidth: 220,
    minWidth: 140,
    renderCell: cellParams => (
      <Tooltip arrow placement="bottom" title={cellParams.value}>
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          {cellParams.value}
        </Box>
      </Tooltip>
    ),
    width: 220,
  },
];

const SelectCustomers = ({
  areAllCustomersAllowed,
  customersList,
  enabledOptions,
  isCustomersListPending,
  quickFilterInput,
  selectedCustomers,
  setAreAllCustomersAllowed,
  setHasChangesToStepThree,
  setSelectCustomersSearchTerm,
  setSelectedCustomers,
}) => {
  const isCreateCarrierAuthEnabled = EvaluateFlag(
    process.env.REACT_APP_LD_CREATE_CARRIER_AUTHORIZATIONS
  );

  const initialRender = useRef(true);

  const [quickFilterValues, setQuickFilterValues] = useState([]);

  useEffect(() => {
    if (R.equals(areAllCustomersAllowed, 'true') && !isCustomersListPending) {
      setSelectedCustomers(customersList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomersListPending]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (setHasChangesToStepThree) {
      setHasChangesToStepThree(true);
    }
  }, [enabledOptions, setHasChangesToStepThree]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setQuickFilterValues(
        quickFilterInput
          .split(' ')
          .map(v => v.trim())
          .filter(Boolean)
      );
    }, 200);
    return () => clearTimeout(debounceTimeout);
  }, [quickFilterInput]);

  const handleClearQuickFilter = () => {
    setSelectCustomersSearchTerm('');
  };

  const filteredRows = useMemo(() => {
    let rows = customersList;

    // Filter by customer name (second TextField)
    if (quickFilterInput.trim()) {
      const searchTerms = quickFilterInput.toLowerCase().split(' ').filter(Boolean);
      rows = rows.filter(customer => {
        const customerName = customer.customerName?.toLowerCase() || '';
        const customerIdn = customer.customerIdn?.toLowerCase() || '';

        return searchTerms.every(term => customerName.includes(term) || customerIdn.includes(term));
      });
    }

    return rows;
  }, [customersList, quickFilterInput]);

  const filterModel = useMemo(() => ({ items: [], quickFilterValues }), [quickFilterValues]);

  const selectedRowIds = useMemo(
    () => selectedCustomers?.map(c => c.customerId) || [],
    [selectedCustomers]
  );
  const handleSelection = useCallback(
    rowSelectionModel => {
      const selectedIds = new Set(rowSelectionModel.map(id => String(id)));
      const filteredRowIds = new Set(filteredRows.map(c => String(c.customerId)));

      const selectedOutsideCurrentFilter = selectedCustomers.filter(
        customer => !filteredRowIds.has(String(customer.customerId))
      );

      const selectedInCurrentFilter = filteredRows.filter(customer =>
        selectedIds.has(String(customer.customerId))
      );

      setSelectedCustomers(
        R.uniqBy(R.prop('customerId'), [
          ...selectedOutsideCurrentFilter,
          ...selectedInCurrentFilter,
        ])
      );
    },
    [filteredRows, selectedCustomers, setSelectedCustomers]
  );

  return (
    <>
      <Divider />
      <Stack px={3} sx={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}>
        {isCreateCarrierAuthEnabled ? (
          <>
            <Typography fontWeight={700} sx={{ mb: 1, mt: 3 }} variant="body1">
              Restrict Customers
            </Typography>
            <Typography variant="body2">
              Restrict this carrier to specific customers? When set, the carrier(s) can load only
              for the customers you list at the location(s) being authorized.
            </Typography>
            <RadioGroup
              aria-labelledby="customer-selecttion-group"
              name="customer-selection"
              onChange={event => {
                setAreAllCustomersAllowed(event.target.value);
                if (R.equals(event.target.value, 'true')) {
                  setSelectedCustomers(customersList);
                } else setSelectedCustomers([]);
              }}
              sx={{ py: 2 }}
              value={areAllCustomersAllowed}
            >
              <FormControlLabel
                control={<Radio />}
                label={
                  <Typography variant="body1">
                    Allow loading for <Typography variant="body1bold">all</Typography> customers
                  </Typography>
                }
                value="true"
              />
              <FormControlLabel
                control={<Radio />}
                label={
                  <Typography variant="body1">
                    Allow loading for <Typography variant="body1bold">selected</Typography>{' '}
                    customers
                  </Typography>
                }
                value="false"
              />
            </RadioGroup>
            {R.equals(areAllCustomersAllowed, 'false') && (
              <>
                <Box
                  sx={{
                    border: '1px solid #0288D1',
                    borderRadius: '4px',
                    display: 'flex',
                    mb: 2,
                    p: 2,
                  }}
                >
                  <Box alignItems="start" display="flex" mb={1}>
                    <InfoOutlinedIcon color="info" fontSize="small" sx={{ mr: 0.5 }} />
                  </Box>
                  <Typography
                    color="textSecondary"
                    sx={{ color: '#0288D1', ml: 1 }}
                    variant="body2"
                  >
                    Allowed customers must be authorized at the selected location(s).{' '}
                    <strong>See Customer Authorizations.</strong>
                  </Typography>
                </Box>
                <FormControl>
                  <TextField
                    fullWidth
                    label="Search"
                    onChange={e => setSelectCustomersSearchTerm(e.target.value)}
                    placeholder="Customer"
                    slotProps={{
                      input: {
                        endAdornment: quickFilterInput ? (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="clear quick filter"
                              edge="end"
                              onClick={handleClearQuickFilter}
                              size="small"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ mb: 2, mt: 1 }}
                    value={quickFilterInput}
                    variant="outlined"
                  />
                </FormControl>
                <Box
                  sx={{
                    borderRadius: 1,
                    display: 'flex',
                    flex: 1,
                    minHeight: 200,
                    overflow: 'hidden',
                    width: '100%',
                  }}
                >
                  <DataGridPremium
                    checkboxSelection
                    columns={columns}
                    disableColumnFilter
                    disableColumnMenu
                    disableColumnSelector
                    disableDensitySelector
                    disableMultipleRowSelection
                    filterModel={filterModel}
                    getRowId={row => row.customerId}
                    hideFooterRowCount
                    onRowSelectionModelChange={handleSelection}
                    rowHeight={64}
                    rows={filteredRows}
                    rowSelectionModel={selectedRowIds}
                    sortingOrder={['asc', 'desc']}
                    sx={{
                      '& .MuiDataGrid-main': { overflowX: 'hidden' },
                      '& .MuiDataGrid-virtualScroller': {
                        overflowX: 'hidden',
                        overflowY: filteredRows.length > 0 ? 'auto' : 'hidden',
                      },
                      width: '100%',
                    }}
                  />
                </Box>
              </>
            )}
          </>
        ) : (
          <>
            <FormControl>
              <FormLabel mb={2}>
                <Typography variant="body1">
                  Would you like to restrict Customers? The Carrier restriction function indicates
                  if a carrier is restricted to certain customers within a Customer Authorization.
                </Typography>
              </FormLabel>
              <RadioGroup
                aria-labelledby="customer-selecttion-group"
                name="customer-selection"
                onChange={event => {
                  setAreAllCustomersAllowed(event.target.value);
                  if (R.equals(event.target.value, 'true')) {
                    setSelectedCustomers(customersList);
                  } else setSelectedCustomers([]);
                }}
                sx={{ py: 2 }}
                value={areAllCustomersAllowed}
              >
                <FormControlLabel
                  control={<Radio />}
                  label={
                    <Typography variant="body1">
                      Allow loading for <Typography variant="body1bold">all</Typography> customers
                    </Typography>
                  }
                  value="true"
                />
                <FormControlLabel
                  control={<Radio />}
                  label={
                    <Typography variant="body1">
                      Allow loading for <Typography variant="body1bold">selected</Typography>{' '}
                      customers
                    </Typography>
                  }
                  value="false"
                />
              </RadioGroup>
            </FormControl>
            {R.equals(areAllCustomersAllowed, 'false') && (
              <VirtualizedAutocomplete
                handleChange={val => {
                  setSelectedCustomers(val);
                }}
                optionIdName="customerId"
                optionLabel="customerName"
                options={customersList}
                value={selectedCustomers}
              />
            )}
          </>
        )}
      </Stack>
    </>
  );
};

SelectCustomers.propTypes = {
  areAllCustomersAllowed: PropTypes.string.isRequired,
  customersList: PropTypes.arrayOf(PropTypes.shape).isRequired,
  enabledOptions: PropTypes.arrayOf(PropTypes.shape).isRequired,
  isCustomersListPending: PropTypes.bool.isRequired,
  quickFilterInput: PropTypes.string.isRequired,
  selectedCustomers: PropTypes.arrayOf(PropTypes.shape).isRequired,
  setAreAllCustomersAllowed: PropTypes.func.isRequired,
  setHasChangesToStepThree: PropTypes.func.isRequired,
  setSelectCustomersSearchTerm: PropTypes.func.isRequired,
  setSelectedCustomers: PropTypes.func.isRequired,
};

export default SelectCustomers;
